import { useEffect, useMemo, useState } from "react";

import initWasm, { parse_formula } from "@package/calcite";

export type Op = "Add" | "Subtract" | "Multiply" | "Divide";

export type RoundDirection = "Up" | "Down";

export type ProductIdentifier = {
  product: string;
  field: string;
  offset: number;
};

export type Expression =
  | { Value: number }
  | { Manual: Record<string, never> }
  | { Variable: ProductIdentifier }
  | { UnaryOp: Expression }
  | { BinaryOp: { lhs: Expression; op: Op; rhs: Expression } }
  | { Call: [string, Expression[]] };

// Direction encoding from WASM: -1 = Down, 1 = Up
export function callDirectionToRoundDirection(value: number): RoundDirection | undefined {
  if (value === -1) return "Down";
  if (value === 1) return "Up";
  return undefined;
}

/**
 * Calcite parses `-1` as UnaryOp(Value(1)). Collapse that into a single signed
 * number for display; leave UnaryOp over non-literals as a separate minus.
 */
export function getNegativeNumberLiteral(expr: Expression): number | undefined {
  if (!("UnaryOp" in expr)) return undefined;
  const operand = expr.UnaryOp;
  if (!("Value" in operand)) return undefined;
  return -operand.Value;
}

let wasmInitialized = false;
let wasmInitPromise: Promise<void> | null = null;

export function ensureWasmInitialized(): Promise<void> {
  if (wasmInitialized) return Promise.resolve();
  if (wasmInitPromise) return wasmInitPromise;

  wasmInitPromise = initWasm()
    .then(() => {
      wasmInitialized = true;
    })
    .catch((err) => {
      console.error("Failed to initialize WASM:", err);
      wasmInitPromise = null;
      throw err;
    });

  return wasmInitPromise;
}

export function parseFormula(formulaString: string): Expression | null {
  if (formulaString === "MANUAL") {
    return { Manual: {} as Record<string, never> };
  }

  try {
    return parse_formula(formulaString) as Expression;
  } catch {
    return null;
  }
}

export type UseParsedFormulaResult = {
  ready: boolean;
  ast: Expression | null;
  /** AST nodes that were wrapped in explicit `()` in the source formula string. */
  explicitParenKeys: ReadonlySet<string>;
};

/** Stable key for comparing AST nodes (from WASM / serde JSON shape). */
export function expressionKey(expr: Expression): string {
  return JSON.stringify(expr);
}

/**
 * Substrings inside `()` that are not within `[product]` brackets.
 * Product descriptions may contain parentheses, e.g. `[NAP FP (MRX) (...): PKG]`.
 */
export function extractParenthesizedSubstrings(formula: string): string[] {
  const results: string[] = [];
  let parenDepth = 0;
  let bracketDepth = 0;
  const starts: number[] = [];

  for (let i = 0; i < formula.length; i++) {
    const char = formula[i];
    if (char === "[") {
      bracketDepth++;
    } else if (char === "]" && bracketDepth > 0) {
      bracketDepth--;
    } else if (bracketDepth === 0) {
      if (char === "(") {
        starts.push(i + 1);
        parenDepth++;
      } else if (char === ")") {
        parenDepth--;
        const start = starts.pop();
        if (start !== undefined) {
          results.push(formula.slice(start, i).trim());
        }
      }
    }
  }

  return results;
}

/** Parses each explicit parenthesis group in the formula and returns matching AST keys. */
export function collectExplicitParenKeys(formulaString: string): ReadonlySet<string> {
  const keys = new Set<string>();
  if (formulaString === "MANUAL") {
    return keys;
  }

  for (const inner of extractParenthesizedSubstrings(formulaString)) {
    const ast = parseFormula(inner);
    if (ast) {
      keys.add(expressionKey(ast));
    }
  }

  return keys;
}

/** Whether to wrap an AST node in parentheses when rendering. */
export function shouldWrapExpression(
  expr: Expression,
  parentOp: Op | undefined,
  explicitParenKeys: ReadonlySet<string>,
): boolean {
  if (explicitParenKeys.has(expressionKey(expr))) {
    return true;
  }

  if ("BinaryOp" in expr) {
    const { op } = expr.BinaryOp;
    return !!parentOp && (parentOp === "Multiply" || parentOp === "Divide") && (op === "Add" || op === "Subtract");
  }

  return false;
}

export function useParsedFormula(formulaString: string): UseParsedFormulaResult {
  const [ready, setReady] = useState(wasmInitialized);

  useEffect(() => {
    if (!ready) {
      ensureWasmInitialized().then(() => setReady(true));
    }
  }, [ready]);

  const ast = useMemo(() => (ready ? parseFormula(formulaString) : null), [formulaString, ready]);

  const explicitParenKeys = useMemo(
    () => (ready ? collectExplicitParenKeys(formulaString) : new Set<string>()),
    [formulaString, ready],
  );

  return { ready, ast, explicitParenKeys };
}
